import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import { useEFiling } from '../contexts/EFilingContext';
import { isConfirmFixableBlocker } from '../lib/confirm-fixable-blockers';
import { showsAccountFeeWaiverStatus, type AccountFeeWaiverStatus } from '../lib/account-fee-waiver-status';
import { BlockingMessage } from './BlockingMessage';
import { FeeCalcFailureBlock, feeFailureView, type FeeCalcFailure } from './FeeCalcFailureBlock';
import {
  classifyFeeCalcResponse,
  feeInputFingerprint,
  feeRecordPatch,
  selectResolvedQuote,
  type VendorFeeBreakdown,
} from '../lib/fee-quote-record';
import { feeAmountText } from '../lib/fee-display';
import { useApi, API_BASE_URL } from '../../../api/useApi';

const API_BASE = API_BASE_URL;

/**
 * SPEC-SOR-343: this tab no longer derives fee figures from the response body.
 * Every attempt — each response face and each thrown fetch — is classified by
 * `classifyFeeCalcResponse` and written through `feeRecordPatch` (via
 * `updateCourtFees`, on EVERY outcome), and every dollar figure on the step is
 * read back from the record through `selectResolvedQuote`. A non-resolved
 * outcome therefore leaves the step figure-free: failed/degraded render the
 * failure and its retry control inside the always-mounted `fee-calc-blocked`
 * live region; unresolved keeps its dedicated banner; the authorization
 * checkbox stays disabled until a quote resolves.
 *
 * One outcome never writes: an attempt that arrives after the fee inputs it
 * was priced under changed is dropped whole (the fingerprint guard in
 * `calculateFees`, compared against the provider's live baseline) — the
 * provider's invalidation effect has already cleared the record by then, and
 * an in-flight answer priced under the old inputs must not resurrect it. The
 * comparison reads the baseline through the context, not a component-local
 * mirror: the navigation that changed the input unmounts this tab.
 *
 * Every figure here renders through the shared `feeAmountText` presenter
 * (R16) — the same formatter the Filing Summary rail and the Submit bar use —
 * so the panel's text cannot diverge from theirs at $1,000 and up.
 *
 * SPEC-SOR-424: the request is ORDER-scoped. `POST /api/efiling/fees/calculate`
 * answers the quote persisted for the order its body names, so the body carries
 * the wizard's `orderId` — minted through the context's existing lazy
 * `createDraftOrder` when the draft is still unbound — and no request goes out
 * until an order identity exists. The figures the response resolves are the
 * server's own persisted record, so what this step shows is what the server
 * will honor at submit.
 */

/** The response's legacy vendor fees block, if this face carried one (the patch's only body input). */
function readVendorFees(body: unknown): VendorFeeBreakdown | null {
  const b = body as { fees?: VendorFeeBreakdown } | null | undefined;
  return b?.fees ?? null;
}

/** The response's account-level fee-waiver status, if this face carried one (informational only). */
function readAccountFeeWaiver(body: unknown): AccountFeeWaiverStatus | null {
  const b = body as { accountFeeWaiver?: AccountFeeWaiverStatus | null } | null | undefined;
  return b?.accountFeeWaiver ?? null;
}

interface CourtFeesTabProps {
  brand: BrandConfig;
}

const CourtFeesTab: React.FC<CourtFeesTabProps> = ({ brand }) => {
  const { wizardState, orderId, createDraftOrder, updateCourtFees, getFeeCalcBlockers, setCurrentTab, confirmFlowEnabled, docTypeMetadata, getFeeInputsFingerprint } = useEFiling();
  const { courtFees, documents, parties } = wizardState;
  const { fetchWithAuth } = useApi();

  // Strict envelope readiness (Walter 2026-07-10 R7). The wizard's Next gate
  // normally prevents reaching this step with an incomplete envelope; this is
  // the belt-and-suspenders backstop so fee calculation can NEVER fire against
  // the court with a type-less document or missing required metadata — that
  // request bounces as a raw SOAP fault naming neither the document nor the fix.
  const feeCalcBlockers = getFeeCalcBlockers();
  const feeCalcReady = feeCalcBlockers.length === 0;

  const [loadingFees, setLoadingFees] = useState(false);
  const [feeFailure, setFeeFailure] = useState<FeeCalcFailure | null>(null);
  // Account-level fee-waiver status (SOR-142 R16/R17) — informational surfacing
  // only: rendered independently of the party-level banner and feeding no fee
  // math. It reflects the LAST response that carried a block; a later
  // figure-free face (degraded/failure) legitimately drops it.
  const [accountFeeWaiver, setAccountFeeWaiver] = useState<AccountFeeWaiverStatus | null>(null);
  // The in-flight draft-order mint, so a double-invoked mount effect fires one
  // `POST /api/orders` rather than two (see `calculateFees`).
  const mintRef = useRef<Promise<string> | null>(null);

  const hasFeeWaiverParties = parties.some(p => p.feeWaiverOnFile || p.requestFeesExemption);
  const hasDocuments = documents.length > 0;
  const hasParties = parties.length > 0;
  const county = wizardState.orderInfo.county;

  // The ONLY read path for every displayed amount (SPEC-SOR-343): the resolved
  // quote's record, or null — a cleared, stale or never-written record renders
  // no figures at all, never a fallback split or a coalesced $0.00.
  const quote = selectResolvedQuote(courtFees);

  const calculateFees = useCallback(async () => {
    // The readiness guard stays FIRST: an incomplete envelope neither fires a
    // request nor mints an order.
    if (!hasDocuments || !hasParties || !county || !feeCalcReady) return;

    // The fingerprint of the draft this request BODY is built from. The
    // provider's invalidation effect runs on the same inputs: if they change
    // while this attempt is in flight, it clears the record — and the answer
    // that then arrives is priced under inputs that no longer exist, so it
    // must not land (a resolved $0.00 priced under a since-removed fee-waiver
    // party would resurrect the record and waive the card on a filing that
    // genuinely costs money).
    const firedFingerprint = feeInputFingerprint(wizardState);
    // Drop = compare the fire-time fingerprint against the provider's LIVE
    // baseline at arrival (`getFeeInputsFingerprint`). Deliberately not a
    // component-local mirror: the navigation that changed the input unmounts
    // this tab, so a local ref would have frozen at the fire-time value and
    // waved the stale answer straight through. A `null` baseline (no
    // observation yet) is no mismatch to act on.
    const inputsChangedSinceFire = () => {
      const landedBaseline = getFeeInputsFingerprint();
      return landedBaseline !== null && landedBaseline !== firedFingerprint;
    };

    setLoadingFees(true);
    setFeeFailure(null);

    try {
      // The request is ORDER-scoped: the route answers the quote persisted for
      // the order it names (SPEC-SOR-424), so there is nothing to ask until a
      // draft order exists. `createDraftOrder` is the context's existing lazy
      // minter — the Review Fees step becomes its third caller beside the
      // upload and submit flows — and until it answers, no `fees/calculate`
      // request is on the wire and the step stays figure-free. A failed mint
      // lands in the catch below, so the step stays figure-free and retryable
      // rather than rendering a quote for an order that does not exist.
      //
      // One mint per tab instance: the mount effect is StrictMode-double-
      // invoked in dev, and two concurrent mints would leave an orphan draft
      // order behind (the context's `setOrderId` guard collapses only the
      // binding, not the requests).
      if (!orderId && !mintRef.current) {
        mintRef.current = createDraftOrder().catch((err) => {
          mintRef.current = null;
          throw err;
        });
      }
      const id = orderId ?? (await mintRef.current!);
      if (!id) return;

      // The mint is ASYNC: the inputs the body is built from may have moved
      // while it ran, exactly as they may move while the request is in flight.
      if (inputsChangedSinceFire()) return;

      const res = await fetchWithAuth(`${API_BASE}/api/efiling/fees/calculate`, {
        method: 'POST',
        body: JSON.stringify({ wizardState, county, orderId: id }),
      });

      // A non-JSON error body (proxy/HTML 502) must still classify by status,
      // so the parse is guarded rather than routed to the thrown-fetch face.
      let body: unknown;
      try {
        body = await res.json();
      } catch {
        body = undefined;
      }

      if (inputsChangedSinceFire()) return;

      const outcome = classifyFeeCalcResponse({ ok: res.ok, status: res.status, body });
      // ONE write per attempt, on every outcome that LANDS (the drop above is
      // the one exception) — the resolved face writes the record, every other
      // face clears it and zeroes the legacy fees block.
      updateCourtFees(feeRecordPatch(outcome, readVendorFees(body)));
      setAccountFeeWaiver(readAccountFeeWaiver(body));
      setFeeFailure(feeFailureView(outcome));
    } catch {
      // Thrown fetch — network-level failure, no HTTP response to classify.
      if (inputsChangedSinceFire()) return;

      const outcome = classifyFeeCalcResponse(null);
      updateCourtFees(feeRecordPatch(outcome));
      setFeeFailure(feeFailureView(outcome));
    } finally {
      setLoadingFees(false);
    }
  }, [hasDocuments, hasParties, county, feeCalcReady, wizardState, orderId, createDraftOrder, fetchWithAuth, updateCourtFees, getFeeInputsFingerprint]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO(deps-2026-07): newly detected by react-hooks bump, burn-down separately
    calculateFees();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Envelope not ready — never fire fee-calc; name each gap and route the
  // filer back to the Upload Documents step instead (R3/R4/R7).
  if (!feeCalcReady) {
    return (
      <div className="max-w-2xl">
        <div className="mb-6">
          <h2
            className="text-xl font-bold mb-1"
            style={{ color: brand.colors.secondary, fontFamily: brand.typography.fontFamily.heading }}
          >
            Review the fees
          </h2>
        </div>
        <BlockingMessage
          surface="fee-calc-blocked"
          populated
          heading={
            <>
              <p className="text-sm font-semibold mb-1">
                Finish your documents before we calculate fees
              </p>
              <p className="text-sm mb-2">
                The court needs complete document details to quote your filing fees:
              </p>
            </>
          }
          className="p-4 rounded-lg border"
          style={{ borderColor: '#FDE68A', backgroundColor: '#FFFBEB', color: '#92400E' }}
        >
          <ul className="list-disc list-inside text-sm space-y-0.5 mb-3">
            {feeCalcBlockers.map((err) => (
              <li key={`${err.field}-${err.rule}`}>{err.message}</li>
            ))}
          </ul>
          {/* R84 (confirm flow): when every blocker is a participant-class
              assignment, the fix lives on the Confirm page's Document
              Assignments section — route there, never backward to Upload.
              Mixed or Upload-only gaps keep the Upload route (fixing types
              comes first; the remaining assignment gaps then route forward). */}
          {confirmFlowEnabled &&
          feeCalcBlockers.length > 0 &&
          feeCalcBlockers.every(e => isConfirmFixableBlocker(e, docTypeMetadata)) ? (
            <button
              type="button"
              data-testid="fee-calc-blocked-goto-confirm"
              onClick={() => setCurrentTab('confirm')}
              className="px-4 py-2 rounded-lg text-sm font-medium"
              style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
            >
              Go to Confirm Your Filing
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setCurrentTab('documents')}
              className="px-4 py-2 rounded-lg text-sm font-medium"
              style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
            >
              Go to Upload Documents
            </button>
          )}
        </BlockingMessage>
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      {/* Section header */}
      <div className="mb-6">
        <h2
          className="text-xl font-bold mb-1"
          style={{ color: brand.colors.secondary, fontFamily: brand.typography.fontFamily.heading }}
        >
          Review the fees
        </h2>
        <p className="text-sm" style={{ color: brand.colors.textLight }}>
          Here's what this filing will cost. Court fees go directly to the court — we never mark them up.
        </p>
      </div>

      {/* The fee-calc blocked region is ALWAYS mounted in this view (SPEC-SOR-321
          R5/R6 — the live region is in the document before any blocker fills it).
          Empty while the last attempt resolved (or is in flight); populated with
          the failure + its retry control when a failed/degraded outcome landed,
          in place of any dollar figure (SPEC-SOR-343 R11). */}
      <BlockingMessage
        surface="fee-calc-blocked"
        populated={!!feeFailure}
        className="p-4 rounded-lg border mb-5"
        style={{ borderColor: '#EF4444', backgroundColor: '#FEF2F2' }}
      >
        {feeFailure && (
          <FeeCalcFailureBlock failure={feeFailure} brand={brand} onRetry={calculateFees} />
        )}
      </BlockingMessage>

      {/* Unresolved quote (SOR-87) — the LDS price row did not resolve, so the
          engine and its matrix fallback agree on no service fee. Its own banner,
          no figures, and the authorization checkbox stays disabled. */}
      {courtFees.quoteState === 'unresolved' && !quote && (
        <div
          data-testid="fee-quote-unresolved"
          className="flex items-start gap-3 p-4 rounded-lg border mb-5"
          style={{ borderColor: '#EF4444', backgroundColor: '#FEF2F2' }}
        >
          <svg className="w-5 h-5 mt-0.5 shrink-0" viewBox="0 0 20 20" fill="#EF4444">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
          </svg>
          <div>
            <p className="text-sm font-medium" style={{ color: '#991B1B' }}>
              Fee resolution error
            </p>
            <p className="text-sm mt-0.5" style={{ color: '#B91C1C' }}>
              We could not determine the service fee for this filing. Please contact support.
            </p>
          </div>
        </div>
      )}

      {/* Fees hero — figures ONLY from the resolved record (no record: no hero,
          no dollar figure anywhere on the step). */}
      {(quote !== null || loadingFees) && (
        <div
          className="text-center py-7 px-6 mb-6"
          style={{
            background: `linear-gradient(135deg, ${brand.colors.secondary} 0%, ${brand.colors.secondaryLight} 100%)`,
            borderRadius: '12px',
          }}
        >
          <div
            className="text-xs font-semibold uppercase tracking-widest mb-1"
            style={{ color: 'rgba(255,255,255,0.8)' }}
          >
            Total
          </div>
          {quote !== null && !loadingFees ? (
            <div
              className="font-extrabold"
              style={{
                color: '#FFFFFF',
                fontSize: '44px',
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
              }}
            >
              {feeAmountText(quote.total)}
            </div>
          ) : (
            <div
              className="text-2xl font-bold"
              style={{ color: 'rgba(255,255,255,0.6)' }}
            >
              Calculating...
            </div>
          )}
          <div
            className="text-sm mt-1"
            style={{ color: 'rgba(255,255,255,0.8)' }}
          >
            paid when you submit
          </div>
        </div>
      )}

      {/* Fee breakdown card — every row is the record's own portion, so the
          rendered lines always equal what the calculation resolved and what the
          charge path will read (SPEC-SOR-343 R13/R14). */}
      {quote !== null && (
        <div
          className="rounded-xl overflow-hidden mb-6"
          style={{ border: `1px solid ${brand.colors.border}`, backgroundColor: '#FFFFFF' }}
        >
          <div className="px-5 py-3.5">
            {/* Court filing fee (the quote's court portion) */}
            <FeeRow
              title="Court filing fee"
              subtitle={county ? `Required by ${county.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} Court` : 'Required by the court'}
              amount={quote.court}
              brand={brand}
              loading={loadingFees}
            />

            {/* EFM transaction fee — only when the quote carries a separate EFM portion */}
            {quote.efm !== 0 && (
              <>
                <div style={{ borderTop: `1px solid ${brand.colors.border}` }} />
                <FeeRow
                  title="EFM Fee"
                  subtitle="Electronic filing manager's fee"
                  amount={quote.efm}
                  brand={brand}
                />
              </>
            )}

            <div style={{ borderTop: `1px solid ${brand.colors.border}` }} />

            {/* eFiling service fee */}
            <FeeRow
              title="Court eFiling service"
              subtitle="Our fee to handle the filing"
              amount={quote.ldsService}
              brand={brand}
            />

            <div style={{ borderTop: `1px solid ${brand.colors.border}` }} />

            {/* Payment processing */}
            <FeeRow
              title="Payment processing"
              subtitle="Card processing fee"
              amount={quote.processingFee}
              brand={brand}
            />

            {/* Total row */}
            <div
              className="flex justify-between items-center pt-4 mt-1"
              style={{ borderTop: `2px solid ${brand.colors.border}` }}
            >
              <span className="text-base font-bold" style={{ color: brand.colors.text }}>
                Total
              </span>
              <span
                className="text-xl font-bold"
                style={{ color: brand.colors.link }}
              >
                {feeAmountText(quote.total)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Fee waiver notice */}
      {hasFeeWaiverParties && (
        <div
          className="flex items-start gap-3 p-4 rounded-lg border mb-6"
          style={{ borderColor: brand.colors.primary, backgroundColor: `${brand.colors.primary}08` }}
        >
          <svg className="w-5 h-5 mt-0.5 shrink-0" viewBox="0 0 20 20" fill={brand.colors.primary}>
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
          </svg>
          <p className="text-sm" style={{ color: brand.colors.text }}>
            Fee waiver/exemption has been identified for one or more parties. Court fees may be reduced or waived.
          </p>
        </div>
      )}

      {/* Account-level fee-waiver status (SOR-142 R16/R17) — informational only,
          rendered alongside (never replacing) the party-level banner above. */}
      {showsAccountFeeWaiverStatus(accountFeeWaiver) && (
        <div
          data-testid="account-fee-waiver-status"
          className="flex items-start gap-3 p-4 rounded-lg border mb-6"
          style={{ borderColor: brand.colors.primary, backgroundColor: `${brand.colors.primary}08` }}
        >
          <svg className="w-5 h-5 mt-0.5 shrink-0" viewBox="0 0 20 20" fill={brand.colors.primary}>
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
          </svg>
          <p className="text-sm" style={{ color: brand.colors.text }}>
            <span className="font-semibold">This account is registered as eFiling Fee Waiver (Pro Per).</span>{' '}
            Fee Waiver Status will be verified when you place your filing.
          </p>
        </div>
      )}

      {/* Fee waiver collapsible */}
      <details
        className="rounded-xl mb-6"
        style={{ border: `1px solid ${brand.colors.border}`, backgroundColor: '#FFFFFF' }}
      >
        <summary
          className="px-5 py-3.5 text-sm font-medium cursor-pointer select-none"
          style={{ color: brand.colors.text }}
        >
          Can't afford the court fee?
        </summary>
        <div className="px-5 pb-4">
          <p className="text-sm leading-relaxed" style={{ color: brand.colors.textLight }}>
            If you qualify for a fee waiver, the court won't charge the filing fee.
            You'll need to file a fee-waiver request along with your documents.{' '}
            <a
              href="#"
              className="font-medium"
              style={{ color: brand.colors.link }}
              onClick={e => e.preventDefault()}
            >
              Learn more &rarr;
            </a>
          </p>
        </div>
      </details>

      {/* Authorization checkbox — enabled only while a quote is resolved (there
          is no total to authorize otherwise). */}
      <label
        className="flex items-start gap-3 p-4 rounded-xl cursor-pointer transition-colors"
        style={{
          border: `1px solid ${courtFees.authorizePayment ? brand.colors.primary : brand.colors.border}`,
          backgroundColor: courtFees.authorizePayment ? `${brand.colors.primary}06` : '#FFFFFF',
        }}
      >
        <input
          type="checkbox"
          checked={courtFees.authorizePayment}
          onChange={e => updateCourtFees({ authorizePayment: e.target.checked })}
          disabled={quote === null}
          className="mt-0.5 w-4 h-4 rounded"
          style={{ accentColor: brand.colors.primary }}
        />
        <div>
          {/* One readable sentence covering BOTH halves of the authorization —
              advancing the court fee and charging the total (Walter R41). */}
          <span className="text-sm font-semibold" style={{ color: brand.colors.text }}>
            I authorize {brand.company.name} to advance and pay the court filing fee on my
            behalf, and to charge my payment method for the total above.
          </span>
          <p className="text-xs mt-1" style={{ color: brand.colors.textLight }}>
            You'll pick the card on the next step.
          </p>
        </div>
      </label>
    </div>
  );
};

interface FeeRowProps {
  title: string;
  subtitle: string;
  // Always present: rows render only from the resolved record's five numbers,
  // so the WOL-386 undefined-into-toFixed coalescing is gone for good.
  amount: number;
  brand: BrandConfig;
  loading?: boolean;
}

const FeeRow: React.FC<FeeRowProps> = ({ title, subtitle, amount, brand, loading }) => (
  <div className="flex justify-between items-center py-3.5">
    <div>
      <div className="text-sm font-medium" style={{ color: brand.colors.text }}>
        {title}
      </div>
      <div className="text-xs mt-0.5" style={{ color: brand.colors.textLight }}>
        {subtitle}
      </div>
    </div>
    <div className="text-sm font-semibold" style={{ color: brand.colors.text }}>
      {loading ? (
        <span style={{ color: brand.colors.textLight }}>...</span>
      ) : (
        feeAmountText(amount)
      )}
    </div>
  </div>
);

export default CourtFeesTab;
